import { Dispatch } from 'react';
import { AppThunkAction } from 'src/store';
import { alertSnackbar } from 'src/store/ui/actions';
import { GetErrorCodeMessage } from 'src/constants/errorConsts/errorCodeMessages';
import { IgnoreTrackingErrorMessagesSet } from 'src/constants/errorConsts/errorCodeSets';

interface ExecuteFunctionResponse {
  errors?: Array<string>;
}

interface ExecuteWithSnackbarOptions<T> {
  params: any;
  executeFunction: (params: any) => Promise<T & ExecuteFunctionResponse>;
  checkFunction?: (params: any) => boolean;
  successMessage: string;
  errorMessage: string; // this is a fallback error message if one is not determined from reponse
  dispatch: Dispatch<any>;
  disableApiFailureNotification?: boolean;
}

const showSuccessAlertSnackbar = (
  message: string,
): AppThunkAction => async dispatch => {
  dispatch(
    alertSnackbar({
      successMessage: message,
    }),
  );
};

const showFailAlertSnackbar = (
  message: string,
): AppThunkAction => async dispatch => {
  dispatch(
    alertSnackbar({
      errorMessage: message,
    }),
  );
};

/**
 * Determine if this error can be ignored for tracking purposes
 * @param errorData info about error used to determine if error is ignoreable
 */
const isErrorIgnoreableForTracking = ({
  message,
}: {
  message?: string;
}): boolean => {
  if (message) {
    return Boolean(IgnoreTrackingErrorMessagesSet[message]);
  }

  return false;
};

class ResultError {
  response: any;

  constructor(value: any) {
    this.response = value;
  }
}

export interface ApiWithNotificationResponseType<T> {
  status: boolean;
  data: T;
  errorMessage: string;
}

// Displays notification in snackbar. Still in testing process. Needs to be documented how to be used.
export const notify = ({
  status,
  successMessage = '',
  error,
  errorMessage,
  checkFunction,
  dispatch,
}: {
  status: 'success' | 'error';
  successMessage?: string;
  errorMessage?: string;
  error?: any;
  dispatch: Dispatch<any>;
  checkFunction?: (params: any) => boolean;
  response?: any;
}) => {
  if (status === 'success') {
    if ((checkFunction && checkFunction(result)) || !checkFunction) {
      if (successMessage) {
        dispatch(showSuccessAlertSnackbar(successMessage));
      }
    }
  } else {
    let message;

    // try to get the message based on the error response code
    if (error?.response?.code) {
      message = GetErrorCodeMessage(error.response.code);
    } else if (error?.response?.data?.code) {
      message = GetErrorCodeMessage(error.response.data.code);
    }

    // if no message is defined for this error response code set to the fallback
    // errorMessage param
    if (!message) {
      message = errorMessage;
    }

    if (message) {
      dispatch(showFailAlertSnackbar(message));
    }

    // check if error can be ignored for tracking purposes. If so
    // dont need to log console error
    if (isErrorIgnoreableForTracking({ message })) {
      return;
    }

    if (error.response) {
      console.error('api error response', error.response);
    } else {
      console.error('unexpected error', error);
    }
  }
};

export const CallApiWithNotification = async <T>(
  executionOptions: ExecuteWithSnackbarOptions<T>,
): Promise<ApiWithNotificationResponseType<T>> => {
  const {
    executeFunction,
    checkFunction,
    params,
    successMessage,
    errorMessage,
    dispatch,
    disableApiFailureNotification,
  } = executionOptions;

  try {
    const result = await executeFunction(params);
    if (result) {
      if ((checkFunction && checkFunction(result)) || !checkFunction) {
        if (successMessage) {
          dispatch(showSuccessAlertSnackbar(successMessage));
        }

        return {
          status: true,
          data: result,
          errorMessage: '',
        };
      }
    }
    if (result.errors && result.errors.length > 0) {
      // if the result has an errors property that includes an array of items throw the first error
      // to be handle by snackbar
      throw new ResultError(result.errors[0]);
    } else {
      throw Error();
    }
  } catch (error) {
    let message = '';
    // try to get the message based on the error response code
    if (error?.response?.code) {
      message = GetErrorCodeMessage(error.response.code);
    } else if (error?.response?.data?.code) {
      message = GetErrorCodeMessage(error.response.data.code);
    } else if (error instanceof ResultError) {
      message = error?.response;
    } else if (error?.response?.data?.message) {
      // check if error message was passed as response
      message = error?.response.data.message;
    }

    // if no message is defined for this error response code set to the fallback
    // errorMessage param
    if (!message) {
      message = errorMessage;
    }

    if (message && !disableApiFailureNotification) {
      dispatch(showFailAlertSnackbar(message));
    }

    // check if error can be ignored for tracking purposes. If so
    // dont need to log console error
    if (isErrorIgnoreableForTracking({ message })) {
      if (error.response) {
        console.error('api error response', error.response);
      } else {
        console.error('unexpected error', error);
      }
    }

    throw new Error(message);
  }
};
